import React from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import { queryKeys } from "~/api/cache"
import ToggleButton from "~/components/ToggleButton"
import type { IPart } from "~/containers/Proposal/Setup/Form/setup-steps-types"
import { isTpoProductSelectedState } from "~/containers/Proposal/state/loan-products/tpo-loan-product"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import type {
  GetSelectIncentivesModalQuery,
  ProposalIncentive,
} from "~/graphql/client"
import {
  IncentiveCategory,
  useGetSelectIncentivesModalQuery,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { FinanceType } from "~/utils/finance/constants"

import { SetupMenuFormFooter } from "../SetupMenuFooter"

const IncentivesSelection: IPart[`form`] = () => {
  const proposal = useProposalContext()
  const { client } = useGraphqlClient()
  const queryClient = useQueryClient()
  const onError = useDispatchError()
  const incentivesQuery = useGetSelectIncentivesModalQuery(
    client,
    { proposalId: proposal.local.id },
    {
      onError,
    },
  )
  const incentives = incentivesQuery.data?.getSelectIncentivesModal ?? {
    listed: [],
    unlisted: [],
  }

  const [showDetails, setShowDetails] = React.useState<string | null>(null)

  const toggleDetails = (id: string) => {
    setShowDetails((prev) => (prev === id ? null : id))
  }

  const toggleIncentive = (proposalIncentive: ProposalIncentive) => {
    const updatedIncentives = incentives.listed.map((possibleIncentive) => {
      if (possibleIncentive.incentiveId !== proposalIncentive.incentiveId) {
        return possibleIncentive
      }

      let { applyToLoan } = proposalIncentive
      if (applyToLoan && proposalIncentive.incentive.isSungageRebate) {
        applyToLoan = false
      }

      return {
        ...possibleIncentive,
        showOnBrochure: !proposalIncentive.showOnBrochure,
        applyToLoan,
      }
    })

    queryClient.setQueryData(
      queryKeys.legacy.graphql.selectIncentivesModal(proposal.local.id),
      (queryData): GetSelectIncentivesModalQuery => {
        const data = queryData as GetSelectIncentivesModalQuery

        return {
          getSelectIncentivesModal: {
            ...data.getSelectIncentivesModal,
            listed: updatedIncentives,
          },
        }
      },
    )

    proposal.changeLocal({
      incentives: [...incentives.unlisted, ...updatedIncentives],
    })
  }

  // could move this to form footer
  React.useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: queryKeys.legacy.graphql.selectIncentivesModal(
          proposal.local.id,
        ),
      })
    }
  }, [queryClient, proposal.local.id])

  const isTpo = useRecoilValue(isTpoProductSelectedState)
  const isCash = proposal.local.financeType === FinanceType.CASH
  const isLightReach = proposal.local.financeType === FinanceType.LIGHT_REACH
  const isFullyIneligibleTpo = !isCash && !isLightReach && isTpo
  const isNewCustomerIncentiveDisabled = isFullyIneligibleTpo || isLightReach

  return (
    <form id="incentives-selection-form">
      <Wrapper>
        {[
          ...incentives.listed.map((proposalIncentive) => {
            const isIncentiveDisabled =
              isFullyIneligibleTpo ||
              (isLightReach &&
                proposalIncentive.incentive.incentiveCategory !==
                  IncentiveCategory.battery)

            return (
              <article key={proposalIncentive.incentiveId}>
                <div className="flex items-center justify-between">
                  <h4
                    onClick={() => toggleDetails(proposalIncentive.incentiveId)}
                    aria-label="detail-toggle"
                  >
                    {proposalIncentive.incentive.customDisplayName ??
                      proposalIncentive.incentive.incentiveName}
                  </h4>
                  <ToggleButton
                    id={`${proposalIncentive.incentiveId}-toggle`}
                    isDisabled={isIncentiveDisabled}
                    isActive={
                      !isIncentiveDisabled && proposalIncentive.showOnBrochure
                    }
                    setAction={() => {
                      if (isIncentiveDisabled) return
                      toggleIncentive(proposalIncentive)
                    }}
                    label={``}
                  />
                </div>
                <div hidden={showDetails !== proposalIncentive.incentiveId}>
                  <h5>Summary</h5>
                  <span>{proposalIncentive.incentive.summary}</span>
                  <h5>Requirements</h5>
                  <span>{proposalIncentive.incentive.requirements}</span>
                </div>
              </article>
            )
          }),
          <article key="new-customer-incentive">
            <div className="flex items-center justify-between">
              <h4
                onClick={() => toggleDetails(`new-customer-incentive`)}
                aria-label="detail-toggle"
              >
                New Customer Incentive
              </h4>
              <ToggleButton
                isDisabled={isNewCustomerIncentiveDisabled}
                id={`new-customer-incentive-toggle`}
                isActive={
                  !isNewCustomerIncentiveDisabled &&
                  proposal.local.newCustomerIncentive
                }
                setAction={() => {
                  if (isNewCustomerIncentiveDisabled) return
                  proposal.changeLocal({
                    newCustomerIncentive: !proposal.local.newCustomerIncentive,
                  })
                }}
                label={``}
              />
            </div>
            <div hidden={showDetails !== `new-customer-incentive`}>
              <h5>Summary</h5>
              <span>
                The New Customer Incentive is an optional rebate from Powur to
                the homeowner. The incentive is valued at 12 times the initial
                monthly loan payment. If a GoodLeap Flex Pay 18-month or 36-month
                option is selected, the New Customer Incentive will cover 18 or
                36 months respectively.
              </span>
            </div>
          </article>,
        ]}
      </Wrapper>
      <SetupMenuFormFooter
        entitiesToUpdate={[proposal]}
        isLoading={proposal.isLoading}
      />
    </form>
  )
}

export default IncentivesSelection

const Wrapper = styled.section`
  & > article > div {
    > span {
      font-size: 12pt;
    }
    > h5 {
      font-size: 12pt;
      padding-top: 1rem;
    }
  }
  & > article > div > h4 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: help;
    color: var(--blue-dark-font);
    padding: 12px 0;
  }
`
